import { validDailyTiming } from '../time/DailyChallengeFreshness';

export type DailyChallengeReward = {
  quantity?: number;
  displayText?: string;
  buildingType: number;
  noboType?: number;
  name: string;
  image: string;
};
export type DailyChallengeRewardClaim = {
  kind: 'daily' | 'weekly';
  claimedAt?: number;
  week?: string;
  reward: DailyChallengeReward;
};

function validRewardClaim(value: unknown): value is DailyChallengeRewardClaim {
  if (!value || typeof value !== 'object') return false;
  const claim = value as Omit<DailyChallengeRewardClaim, 'kind'> & { kind: unknown };
  return (
    (claim.kind === 'daily' || claim.kind === 'weekly') &&
    (claim.claimedAt === undefined ||
      (Number.isSafeInteger(claim.claimedAt) &&
        claim.claimedAt > 0 &&
        claim.claimedAt <= 8_640_000_000_000_000)) &&
    (claim.kind === 'weekly'
      ? typeof claim.week === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(claim.week)
      : claim.week === undefined) &&
    validReward(claim.reward)
  );
}
export const defaultDailyReward: DailyChallengeReward = {
  buildingType: 3020,
  name: 'regular treasure chest',
  image: '/legacy/build-menu/products/skattkista.jpg',
};
export const defaultWeeklyReward: DailyChallengeReward = {
  buildingType: 3021,
  name: 'big red treasure chest',
  image: '/legacy/build-menu/products/skattkista_fet.jpg',
};
function validReward(value: unknown): value is DailyChallengeReward {
  if (!value || typeof value !== 'object') return false;
  const reward = value as DailyChallengeReward;
  return (
    (reward.quantity === undefined ||
      (Number.isInteger(reward.quantity) && reward.quantity >= 1 && reward.quantity <= 100)) &&
    (reward.displayText === undefined ||
      (typeof reward.displayText === 'string' && Array.from(reward.displayText).length <= 160)) &&
    Number.isInteger(reward.buildingType) &&
    (reward.noboType === undefined || (Number.isInteger(reward.noboType) && reward.noboType > 0)) &&
    reward.buildingType !== 0 &&
    reward.buildingType >= -32768 &&
    reward.buildingType <= 32767 &&
    typeof reward.name === 'string' &&
    reward.name.length > 0 &&
    typeof reward.image === 'string' &&
    reward.image.startsWith('/') &&
    !reward.image.startsWith('//')
  );
}
function validWeeklyRewards(value: unknown): boolean {
  return (
    value !== null &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.values(value).every(validReward)
  );
}
export type DailyChallengeRequirement = {
  id: string;
  title: string;
  target: number;
  unit: string;
  progress: number;
  completed: boolean;
};
function validRequirements(value: unknown): value is DailyChallengeRequirement[] {
  if (!Array.isArray(value) || value.length === 0) return false;
  const ids = new Set<string>();
  return value.every((item: unknown) => {
    if (!item || typeof item !== 'object') return false;
    const r = item as DailyChallengeRequirement;
    if (
      typeof r.id !== 'string' ||
      !r.id ||
      ids.has(r.id) ||
      typeof r.title !== 'string' ||
      typeof r.unit !== 'string' ||
      !Number.isFinite(r.target) ||
      r.target <= 0 ||
      !Number.isFinite(r.progress) ||
      r.progress < 0 ||
      r.progress > r.target ||
      typeof r.completed !== 'boolean'
    )
      return false;
    ids.add(r.id);
    return r.completed === r.progress >= r.target;
  });
}
export type DailyChallenges = {
  recovery?: DailyChallengeRequirement & { previousStreak: number };
  requirements?: DailyChallengeRequirement[];
  scheduledDailyReward?: DailyChallengeReward;
  dailyReward: DailyChallengeReward;
  weeklyReward: DailyChallengeReward;
  weeklyRewards: Record<string, DailyChallengeReward>;
  lastDeliveredReward?: DailyChallengeReward;
  pendingReward?: DailyChallengeReward;
  rewardHistory?: DailyChallengeRewardClaim[];
  historyIncomplete?: boolean;

  day: string;
  revision: number;
  resetsAt: number;
  serverTime: number;
  challenge: { id: string; title: string; target: number; unit: string };
  progress: number;
  completed: boolean;
  streak: number;
  stamps: string[];
  weeklyAvailable: string[];
  weeklyClaimed: string[];
  deliveredDaily: number;
  pendingDaily: number;
  metersPerTile: number;
};
export type DailyChallengeClaim = string | { daily: number };
export type DailyChallengeGateway = (
  claim: DailyChallengeClaim | undefined,
  signal: AbortSignal,
) => Promise<DailyChallenges>;

export function parseDailyChallenges(value: unknown): DailyChallenges {
  const root = value as { dailyChallenges?: Partial<DailyChallenges> } | null;
  const p = root?.dailyChallenges;
  if (
    !p ||
    !validDailyTiming(p) ||
    typeof p.completed !== 'boolean' ||
    (p.recovery !== undefined &&
      (!validRequirements([p.recovery]) ||
        !Number.isSafeInteger(p.recovery.previousStreak) ||
        p.recovery.previousStreak <= 0)) ||
    (p.requirements !== undefined &&
      (!validRequirements(p.requirements) ||
        p.completed !== p.requirements.every((r) => r.completed))) ||
    (p.scheduledDailyReward !== undefined && !validReward(p.scheduledDailyReward)) ||
    (p.dailyReward !== undefined && !validReward(p.dailyReward)) ||
    (p.weeklyReward !== undefined && !validReward(p.weeklyReward)) ||
    (p.pendingReward !== undefined && !validReward(p.pendingReward)) ||
    (p.lastDeliveredReward !== undefined && !validReward(p.lastDeliveredReward)) ||
    (p.rewardHistory !== undefined &&
      (!Array.isArray(p.rewardHistory) || !p.rewardHistory.every(validRewardClaim))) ||
    (p.historyIncomplete !== undefined && typeof p.historyIncomplete !== 'boolean') ||
    (p.weeklyRewards !== undefined && !validWeeklyRewards(p.weeklyRewards)) ||
    !p.challenge ||
    typeof p.challenge.id !== 'string' ||
    typeof p.challenge.title !== 'string' ||
    typeof p.challenge.unit !== 'string' ||
    !Number.isFinite(p.challenge.target) ||
    p.challenge.target <= 0 ||
    ![
      p.revision,
      p.resetsAt,
      p.serverTime,
      p.progress,
      p.streak,
      p.deliveredDaily,
      p.pendingDaily,
      p.metersPerTile,
    ].every((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0) ||
    ![p.stamps, p.weeklyAvailable, p.weeklyClaimed].every(
      (v) => v == null || (Array.isArray(v) && v.every((d) => typeof d === 'string')),
    )
  ) {
    throw new Error('Invalid daily challenge response');
  }
  return {
    ...p,
    requirements: p.requirements ?? [
      { ...p.challenge, progress: p.progress, completed: p.completed },
    ],
    dailyReward: p.dailyReward ?? defaultDailyReward,
    weeklyReward: p.weeklyReward ?? defaultWeeklyReward,
    weeklyRewards: p.weeklyRewards ?? {},
    stamps: p.stamps ?? [],
    weeklyAvailable: p.weeklyAvailable ?? [],
    weeklyClaimed: p.weeklyClaimed ?? [],
  } as DailyChallenges;
}

export function dailyRewardItems(reward: DailyChallengeReward): string {
  return `${String(reward.quantity ?? 1)} ${reward.name}`;
}

export function dailyRewardDescription(reward: DailyChallengeReward): string {
  const description = reward.displayText?.trim() ?? '';
  return description.length > 0 ? description : dailyRewardItems(reward);
}
